import { fromArrayBuffer } from 'geotiff';
import { createField, type GridSpec, type ScalarField } from '../sim/grid.ts';

/**
 * Terrain elevation from Kartverket's WCS (DTM, EPSG:25833, CC BY 4.0). The service sends
 * `Access-Control-Allow-Origin: *`, so the browser fetches it directly.
 *
 * Requests are capped at MAX_TILE_PX per axis, so larger grids are fetched as tiles and
 * stitched. Each tile's bbox is an exact multiple of the cell size, so the service returns
 * pixels aligned 1:1 with our grid cells.
 */
const WCS_BASE = 'https://wcs.geonorge.no/skwms1/wcs.hoyde-dtm-nhm-25833';
const WCS_COVERAGE = 'nhm_dtm_topo_25833';

export const MAX_TILE_PX = 2000;
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 3_000;
const TIMEOUT_MS = 60_000;
const NODATA_THRESHOLD = -1000;

export interface TileRequest {
  col0: number;
  row0: number;
  cols: number;
  rows: number;
}

export function buildWcsUrl(spec: GridSpec, tile: TileRequest): string {
  const minX = spec.minX + tile.col0 * spec.cellSize;
  const maxX = minX + tile.cols * spec.cellSize;
  const maxY = spec.maxY - tile.row0 * spec.cellSize;
  const minY = maxY - tile.rows * spec.cellSize;
  // Built by hand: URLSearchParams encodes ':' and ',' which the service rejects.
  return (
    `${WCS_BASE}?SERVICE=WCS&VERSION=1.0.0&REQUEST=GetCoverage&COVERAGE=${WCS_COVERAGE}` +
    `&CRS=EPSG:25833&BBOX=${minX},${minY},${maxX},${maxY}` +
    `&WIDTH=${tile.cols}&HEIGHT=${tile.rows}&FORMAT=GeoTIFF` +
    // Default is nearest neighbour, which aliases the 1 m DTM into stair-steps at coarser cell
    // sizes (striped shading, noisy slope). Bilinear halves that roughness; verified to cause
    // no sub-pixel shift.
    `&INTERPOLATION=bilinear`
  );
}

export function planTiles(spec: GridSpec, maxPx = MAX_TILE_PX): TileRequest[] {
  const tiles: TileRequest[] = [];
  for (let row0 = 0; row0 < spec.rows; row0 += maxPx) {
    for (let col0 = 0; col0 < spec.cols; col0 += maxPx) {
      tiles.push({
        col0,
        row0,
        cols: Math.min(maxPx, spec.cols - col0),
        rows: Math.min(maxPx, spec.rows - row0),
      });
    }
  }
  return tiles;
}

export interface LoadProgress {
  done: number;
  total: number;
}

/** Fetch the elevation field for a grid. NoData (sea, gaps) becomes 0 m. */
export async function fetchElevation(
  spec: GridSpec,
  opts: {
    signal?: AbortSignal;
    onProgress?: (p: LoadProgress) => void;
    /** Delay between retries (ms); tests shorten it. */
    retryDelayMs?: number;
  } = {},
): Promise<ScalarField> {
  const field = createField(spec);
  const tiles = planTiles(spec);
  let done = 0;
  opts.onProgress?.({ done, total: tiles.length });
  // Sequential on purpose: the service is shared and throttles under parallel load.
  for (const tile of tiles) {
    const raster = await fetchTile(
      buildWcsUrl(spec, tile),
      tile,
      opts.signal,
      opts.retryDelayMs ?? RETRY_DELAY_MS,
    );
    for (let r = 0; r < tile.rows; r++) {
      const dst = (tile.row0 + r) * spec.cols + tile.col0;
      for (let c = 0; c < tile.cols; c++) {
        const v = raster[r * tile.cols + c]!;
        field.data[dst + c] = Number.isFinite(v) && v > NODATA_THRESHOLD ? v : 0;
      }
    }
    opts.onProgress?.({ done: ++done, total: tiles.length });
  }
  return field;
}

async function fetchTile(
  url: string,
  tile: TileRequest,
  signal: AbortSignal | undefined,
  retryDelayMs: number,
): Promise<ArrayLike<number>> {
  const cached = await readCache(url);
  if (cached) {
    try {
      return await decodeTile(cached, tile);
    } catch {
      // A bad cache entry: fall through and fetch it again.
    }
  }
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) await delay(retryDelayMs, signal);
    try {
      const timeout = AbortSignal.timeout(TIMEOUT_MS);
      const res = await fetch(url, {
        signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
      });
      if (res.status === 502 || res.status === 503 || res.status === 504) {
        lastError = new Error(`Kartverket WCS ${res.status}`);
        continue;
      }
      if (!res.ok) throw new Error(`Kartverket WCS error ${res.status} ${res.statusText}`);
      const type = res.headers.get('content-type') ?? '';
      if (type.includes('xml')) {
        throw new Error(`Kartverket WCS error: ${(await res.text()).slice(0, 200)}`);
      }
      const buffer = await res.arrayBuffer();
      const band = await decodeTile(buffer, tile);
      await writeCache(url, buffer);
      return band;
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
      if (!isRetryable(err)) throw err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Kartverket WCS failed');
}

async function decodeTile(buffer: ArrayBuffer, tile: TileRequest): Promise<ArrayLike<number>> {
  const tiff = await fromArrayBuffer(buffer);
  const image = await tiff.getImage();
  if (image.getWidth() !== tile.cols || image.getHeight() !== tile.rows) {
    throw new Error(
      `WCS returned ${image.getWidth()}×${image.getHeight()}, expected ${tile.cols}×${tile.rows}`,
    );
  }
  const [band] = await image.readRasters();
  if (!band || typeof band === 'number') throw new Error('WCS returned no raster data');
  return band as ArrayLike<number>;
}

/**
 * Terrain tiles are cached in the browser (Cache Storage), keyed by request URL, so reloading an
 * area — or loading it while Kartverket's WCS is slow or down, which happens — needs no network.
 * Elevation data doesn't change, so entries never expire. Unavailable (Node, private windows,
 * blocked storage) → no caching, silently.
 */
const TILE_CACHE = 'snow-sim-dtm-v1';

async function readCache(url: string): Promise<ArrayBuffer | null> {
  try {
    if (typeof caches === 'undefined') return null;
    const hit = await (await caches.open(TILE_CACHE)).match(url);
    return hit ? await hit.arrayBuffer() : null;
  } catch {
    return null;
  }
}

async function writeCache(url: string, buffer: ArrayBuffer): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const res = new Response(buffer, { headers: { 'content-type': 'image/tiff' } });
    await (await caches.open(TILE_CACHE)).put(url, res);
  } catch {
    // Quota exceeded or storage blocked: caching is best-effort.
  }
}

/**
 * Timeouts and network-level failures are retried. fetch rejects with a TypeError for the latter,
 * which includes the service's occasional gateway error pages: they carry no CORS header, so the
 * browser can only report them as a failed (CORS-blocked) request.
 */
function isRetryable(err: unknown): boolean {
  return (err instanceof DOMException && err.name === 'TimeoutError') || err instanceof TypeError;
}

function delay(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(signal.reason);
    });
  });
}
